import PropTypes from "prop-types";
import React, { useReducer } from "react";

import API from "../../API";
import {
  POPULATE_PROMO_CODES,
  POPULATE_PROMO_CODES_COUNT,
  POPULATE_PROMO_GROUPS,
  POPULATE_PROMO_GROUPS_COUNT,
  POPULATE_PROMO_CODES_BY_GROUP,
  SET_ACTIVE_PROMO_CODE,
  SET_ACTIVE_PROMO_GROUP,
} from "../types";

import PromoContext from "./PromoContext";
import promoReducer from "./PromoReducer";
import { PROMO_CODE_TYPES } from "../../constants";

const PromoState = ({ children }) => {
  const initialState = {
    promoCodes: [],
    promoCodesCount: 0,
    promoGroups: [],
    promoGroupsCount: 0,
    promoCodesByGroup: [],
    activePromoCode: null,
    activePromoGroup: null,
  };

  const [state, dispatch] = useReducer(promoReducer, initialState);

  const codeAlreadyExists = async (code = null) => {
    if (!code) return false;
    try {
      const resArr = await Promise.all(
        PROMO_CODE_TYPES.map((type) => API.billing.listCoupons(type, { code })),
      );
      return resArr.some((val) => val?.results?.length);
    } catch (err) {
      console.error(err);
      return true;
    }
  };

  const groupNameAlreadyExists = async (groupName = null) => {
    if (!groupName) return false;
    try {
      const resArr = await API.billing.listCouponGroups({ params: { name: groupName } });
      return resArr;
    } catch (err) {
      console.error(err);
      return true;
    }
  };

  const getPromoGroups = async () => {
    try {
      const groups = await API.billing.listCouponGroups();
      dispatch({
        type: POPULATE_PROMO_GROUPS,
        payload: groups.results,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const getPromoCodesByGroup = async (
    groupId = null,
    promoType = "discount",
    sortDirection = null,
    pageSize = 100,
    page = 1,
  ) => {
    if (!groupId) {
      dispatch({ type: POPULATE_PROMO_CODES_BY_GROUP, payload: [] });
      dispatch({ type: POPULATE_PROMO_CODES_COUNT, payload: 0 });
      return;
    }
    try {
      const codes = await API.billing.getCouponsByGroupId(promoType, {
        ...(sortDirection ? { ordering: sortDirection } : {}),
        coupon_group_id: groupId,
        page_size: pageSize,
        page,
      });
      dispatch({
        type: POPULATE_PROMO_CODES_BY_GROUP,
        payload: codes.results,
      });
      dispatch({
        type: POPULATE_PROMO_CODES_COUNT,
        payload: codes.count,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const searchPromoGroups = async (
    searchText = null,
    sortDirection = null,
    pageSize = 100,
    page = 1,
  ) => {
    try {
      const groups = await API.billing.listCouponGroups({
        ...(searchText ? { search: searchText } : {}),
        ...(sortDirection ? { ordering: sortDirection } : {}),
        page_size: pageSize,
        page,
      });
      dispatch({
        type: POPULATE_PROMO_GROUPS,
        payload: groups.results,
      });
      dispatch({
        type: POPULATE_PROMO_GROUPS_COUNT,
        payload: groups.count,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const searchPromoCodes = async (type, search = null, order = null, pageSize = 100, page = 1) => {
    try {
      const codes = await API.billing.listCoupons(type, {
        ...(search ? { code__icontains: search } : {}),
        ...(order ? { ordering: order } : {}),
        page_size: pageSize,
        page,
      });
      dispatch({
        type: POPULATE_PROMO_CODES,
        payload: codes.results,
      });
      dispatch({
        type: POPULATE_PROMO_CODES_COUNT,
        payload: codes.count,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const getActivePromoGroup = async (groupId) => {
    if (!groupId) {
      dispatch({ type: SET_ACTIVE_PROMO_GROUP, payload: null });
      return;
    }
    try {
      const promoGroup = await API.billing.getCouponGroup(groupId);
      setActivePromoGroup(promoGroup);
    } catch (err) {
      console.error(err);
    }
  };

  const setActivePromoGroup = (promoGroup) => {
    dispatch({
      type: SET_ACTIVE_PROMO_GROUP,
      payload: promoGroup,
    });
  };

  const getActivePromoCode = async (id, type) => {
    if (!id) {
      dispatch({ type: SET_ACTIVE_PROMO_CODE, payload: null });
      return;
    }
    try {
      const promo = await API.billing.getCoupon(id, type);
      dispatch({
        type: SET_ACTIVE_PROMO_CODE,
        payload: promo,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const updatePromoCode = async (id, type, data) => {
    try {
      const updatedCode = await API.billing.updateCoupons(id, type, data);
      return updatedCode;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const createPromoCode = async (type, data) => {
    try {
      const newCode = await API.billing.createCoupons(type, data);
      return newCode;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const updateCouponGroup = async (id, data) => {
    try {
      const updatedCode = await API.billing.updateCouponGroup(id, data);
      return updatedCode;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const createCouponGroup = async (data) => {
    try {
      const newCode = await API.billing.createCouponGroup(data);
      return newCode;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  return (
    <PromoContext.Provider
      value={{
        promoCodes: state.promoCodes,
        promoCodesCount: state.promoCodesCount,
        promoGroups: state.promoGroups,
        promoGroupsCount: state.promoGroupsCount,
        promoCodesByGroup: state.promoCodesByGroup,
        activePromoCode: state.activePromoCode,
        activePromoGroup: state.activePromoGroup,
        codeAlreadyExists,
        groupNameAlreadyExists,
        searchPromoCodes,
        searchPromoGroups,
        getActivePromoCode,
        getActivePromoGroup,
        getPromoGroups,
        getPromoCodesByGroup,
        setActivePromoGroup,
        updatePromoCode,
        createPromoCode,
        createCouponGroup,
        updateCouponGroup,
      }}
    >
      {children}
    </PromoContext.Provider>
  );
};

PromoState.propTypes = {
  children: PropTypes.node.isRequired,
};

export default PromoState;
